import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import AnimatedSection from "./AnimatedSection";

interface NewsletterProps {
  apiEndpoint?: string;
  onEmailSubmit?: (email: string) => Promise<void> | void;
}

const Newsletter = ({
  apiEndpoint,
  onEmailSubmit,
}: NewsletterProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email first
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    // Immediately show success toast - optimistic UI
    toast({
      title: "Thank you for joining!",
      description: "You've successfully joined our beta program.",
      duration: 3000,
    });
    
    // Store email for potential error recovery
    const submittedEmail = email;
    
    // Reset form immediately for better UX
    setEmail("");
    
    // Set loading state but keep it brief
    setIsLoading(true);
    
    try {
      // If custom handler is provided, use it
      if (onEmailSubmit) {
        await onEmailSubmit(submittedEmail);
      } 
      else {
        // Using FormSubmit with proper configuration
        await fetch("https://formsubmit.co/ajax/beta@swifquery.com", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({
            email: submittedEmail,
            _subject: "New Beta Signup",
            _template: "table",
            message: `New beta signup request from: ${submittedEmail}`
          }),
        });
        
        console.log("Beta signup submitted:", submittedEmail);
      }

      toast({
        title: "Thank you for joining!",
        description: "You've successfully joined our beta program.",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error submitting email:", error);
      toast({
        title: "Submission failed",
        description: "There was a problem submitting your email. Please try again or contact us directly at beta@swifquery.com.",
        variant: "destructive",
        duration: 5000,
      });
      // If there was an error, give the user their form data back
      setEmail(submittedEmail);
    } finally {
      // Quick loading state to avoid UI jumping
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-background z-[-1]"></div>
      <div className="container-narrow max-w-4xl">
        <AnimatedSection>
          <div className="glass-card rounded-3xl overflow-hidden">
            <div className="p-8 md:p-12 flex flex-col items-center text-center">
              <div className="inline-block px-3 py-1 mb-6 rounded-full bg-primary/20 text-primary text-xs font-medium tracking-wider">
                STAY UPDATED
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-4">
                Join Our Beta
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
                Be among the first to access our beta release. We'll keep you informed about our progress and let you know when we're ready for you to try it out.
              </p>
              <form 
                onSubmit={handleSubmit} 
                className="w-full max-w-md flex flex-col sm:flex-row gap-3"
              >
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="rounded-full px-6 h-12"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Button 
                  type="submit" 
                  className="rounded-full h-12 px-6" 
                  disabled={isLoading}
                >
                  {isLoading ? "Joining..." : "Join Beta"}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-4">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Newsletter;
